import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Check, X, ScanLine, Camera } from "lucide-react";

export const OcrQueue = ({ items, t, onResolve, editable = true, mobile }) => {
  if (!items || items.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full text-zinc-600 gap-2 ${mobile ? "p-8" : "p-6"}`}>
        <ScanLine size={mobile ? 40 : 32} />
        <p className="text-[12px]">{t("noOcr")}</p>
      </div>
    );
  }
  return (
    <div className={`space-y-3 overflow-y-auto no-scrollbar ${mobile ? "p-3 pb-4" : "p-3"}`} data-testid="ocr-queue">
      {items.map((it) => (
        <OcrCard key={it.id} item={it} t={t} onResolve={onResolve} editable={editable} mobile={mobile} />
      ))}
    </div>
  );
};

const OcrCard = ({ item, t, onResolve, editable, mobile }) => {
  const [num, setNum] = useState(item.proposed_number);
  const conf = Math.round(item.confidence * 100);

  useEffect(() => {
    setNum(item.proposed_number);
  }, [item.id, item.proposed_number]);

  const resolve = (action) => {
    onResolve(item.id, num, action);
    toast.success(t("resolved"));
  };

  return (
    <div className="rounded-md border border-zinc-800 bg-[#101013] overflow-hidden" data-testid={`ocr-${item.id}`}>
      <div className={`relative ${mobile ? "h-40" : "h-24"}`}>
        <img src={item.photo_url} alt="ocr" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <span className="absolute top-2 left-2 flex items-center gap-1 text-[9px] font-mono text-zinc-300 bg-black/60 px-1.5 py-0.5 rounded-sm">
          <Camera size={10} /> {item.station_code}
        </span>
        <span
          className="absolute top-2 right-2 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm"
          style={{
            background: "rgba(0,0,0,0.6)",
            color: conf >= 70 ? "#f59e0b" : "#f43f5e",
          }}
        >
          {conf}%
        </span>
      </div>
      <div className="p-3 space-y-2">
        <div>
          <span className="text-[9px] uppercase tracking-widest text-zinc-500">{t("proposed")}</span>
          <Input
            data-testid={`ocr-input-${item.id}`}
            disabled={!editable}
            value={num}
            onChange={(e) => setNum(e.target.value)}
            className={`bg-zinc-900 border-zinc-800 font-mono mt-1 ${mobile ? "h-12 text-base min-h-[44px]" : "h-8"}`}
          />
        </div>
        {editable && (
          <div className={`grid gap-2 ${mobile ? "grid-cols-1" : "grid-cols-2"}`}>
            <Button
              data-testid={`ocr-confirm-${item.id}`}
              onClick={() => resolve("confirm")}
              className={`gap-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-semibold ${mobile ? "h-12 text-sm min-h-[44px]" : "h-8 text-[11px]"}`}
            >
              <Check size={mobile ? 18 : 13} /> {t("confirm")}
            </Button>
            <Button
              data-testid={`ocr-reject-${item.id}`}
              onClick={() => resolve("reject")}
              variant="outline"
              className={`gap-2 bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm ${mobile ? "h-12 text-sm min-h-[44px]" : "h-8 text-[11px]"}`}
            >
              <X size={mobile ? 18 : 13} /> {t("reject")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
